import * as XLSX from "xlsx";

/**
 * Build a timestamp string suitable for filenames: YYYYMMDD_HHmmss
 */
function buildTimestamp() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  );
}

/**
 * Convert an array of row objects to a 2-D array (header row + data rows).
 * Column order is derived from the union of all keys in the rows.
 */
function rowsToMatrix(rows) {
  if (!rows || rows.length === 0) return [[]];
  const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const data = rows.map((row) => headers.map((h) => row[h] ?? ""));
  return [headers, ...data];
}

/**
 * Download individual CSV files — one per view in cacheByView.
 *
 * @param {object} cacheByView   - The cacheByView object from useCacheStreamRouter
 * @param {string} [prefix]      - Optional prefix prepended to each filename
 */
export function exportDashboardCacheAsCsv(cacheByView, prefix = "") {
  const timestamp = buildTimestamp();

  Object.entries(cacheByView || {}).forEach(([viewName, viewData]) => {
    const rows = viewData?.rows ?? [];
    const matrix = rowsToMatrix(rows);

    const csvString = matrix
      .map((row) =>
        row
          .map((cell) => {
            const str = cell === null || cell === undefined ? "" : String(cell);
            // Escape cells that contain commas, quotes, or newlines
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(","),
      )
      .join("\n");

    const fileName = `${prefix ? prefix + "_" : ""}${viewName}_${timestamp}.csv`;
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
}

/**
 * Download a single Excel (.xlsx) file where each view in cacheByView
 * becomes its own worksheet (tab).
 *
 * @param {object} cacheByView   - The cacheByView object from useCacheStreamRouter
 * @param {string} [fileBaseName] - Base filename (without extension)
 */
export function exportDashboardCacheAsExcel(cacheByView, fileBaseName = "dashboard_export") {
  const timestamp = buildTimestamp();
  const wb = XLSX.utils.book_new();

  Object.entries(cacheByView || {}).forEach(([viewName, viewData]) => {
    const rows = viewData?.rows ?? [];
    const matrix = rowsToMatrix(rows);
    const ws = XLSX.utils.aoa_to_sheet(matrix);
    // Sheet names max 31 chars and must not contain certain special chars
    const safeName = viewName.replace(/[\\/*?:[\]]/g, "_").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  });

  const fileName = `${fileBaseName}_${timestamp}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

